import React, { useEffect, useRef, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridOverlay
} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button
} from "@mui/material";
import { AxiosResponse } from "axios";
import html2canvas from "html2canvas";
import http from "../../commonFunctions/httpClient";

interface ReportTableProps {
  title: string;
  endpoint: string;
  columns: GridColDef[];
  filters: Record<string, any>;
}

const ReportTable: React.FC<ReportTableProps> = ({
  title,
  endpoint,
  columns,
  filters
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [filters]);

  const fetchReport = async () => {
    try {
      const response: AxiosResponse = await http.get(endpoint, {
        params: filters
      });
      setRows(response?.data || []); // assuming API structure
    } catch (err) {
      console.error("Failed to load report:", err);
    } finally {
      setLoading(false);
    }
  };

  const captureRef = useRef<HTMLDivElement>(null);

  const takeScreenshot = async () => {
    if (captureRef.current) {
      // Use html2canvas to take the screenshot
      const canvas = await html2canvas(captureRef.current);

      // Convert the canvas to an image URL (base64 encoded)
      const imageUrl = canvas.toDataURL();

      // Create a link element to download the screenshot
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = "kudumbasreeScreenshot.png"; // File name
      link.click();
    }
  };

  // Custom loading indicator
  const CustomLoadingOverlay = () => (
    <GridOverlay>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 3 }}>
        <CircularProgress />
        <Typography sx={{ mt: 1 }}>Loading data...</Typography>
      </Box>
    </GridOverlay>
  );

  // Custom no rows overlay
  const CustomNoRowsOverlay = () => (
    <GridOverlay>
      <Typography sx={{ mt: 3 }}>No data available</Typography>
    </GridOverlay>
  );

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {/* Future Export/Print Buttons */}
        {/* <Box>
          <Button size="small" variant="outlined" sx={{ mr: 1 }}>
            Export CSV
          </Button>
          <Button size="small" variant="outlined">
            Print
          </Button>
        </Box> */}
        <Box>
          <Button variant="contained" size="small" onClick={takeScreenshot} sx={{
            textTransform: "none",
            color: "white",
            borderImage: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
            borderImageSlice: 1,
            backgroundImage: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
            "&:hover": {
              backgroundImage: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
              opacity: 0.9,
            },
          }}>
            Take Screenshot
          </Button>
        </Box>
      </Box>

      <Box sx={{ height: 520, width: "100%" }} ref={captureRef}>
        <DataGrid
          rows={rows.map((row: any, index) => ({ id: index, ...row }))}
          columns={columns}
          loading={loading}
          slots={{
            toolbar: GridToolbar,
            loadingOverlay: CustomLoadingOverlay,
            noRowsOverlay: CustomNoRowsOverlay
          }}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } }
          }}
          disableRowSelectionOnClick
          sx={{
            borderRadius: 2,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f4f4f4',
              fontWeight: 'bold'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#edd1e1'
            }
          }}
        />
      </Box>
    </Paper>
  );
};

export default ReportTable;
